/**
 * Modal de pagamento PIX via EFI Pay.
 *
 * Recebe `kitId`; ao montar chama POST /api/Pay/efi/order pra criar a cobranca,
 * exibe QR code (imagem base64) + codigo copia-e-cola e faz polling
 * de GET /api/Pay/efi/status/{txid} a cada 3s ate status=paid|expired.
 */
import { useEffect, useRef, useState } from 'react';
import { Copy, Check, Loader2, X, QrCode, CheckCircle2, AlertCircle } from 'lucide-react';
import { efiOrder, efiStatus, type EfiPixOrder } from '@/shared/services/payService';
import { useToast } from '@/shared/contexts/ToastContext';

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 30 * 60 * 1000; // 30min hard cap

type Status = 'loading' | 'pending' | 'paid' | 'expired' | 'error';

interface Props {
  kitId: number;
  kitTitle: string;
  kitCash: number;
  /** Cupom de streamer (opcional) — encaminhado ao criar a cobrança PIX. */
  coupon?: string;
  onClose: () => void;
  onPaid: () => void;
}

function formatBRL(value: number) {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatTimer(secs: number) {
  if (secs <= 0) return '00:00';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

export default function EfiPixDialog({ kitId, kitTitle, kitCash, coupon, onClose, onPaid }: Props) {
  const { notify } = useToast();
  const [status, setStatus] = useState<Status>('loading');
  const [order, setOrder] = useState<EfiPixOrder | null>(null);
  const [copied, setCopied] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const startedAt = useRef<number>(0);

  // 1) Cria cobranca PIX
  useEffect(() => {
    let cancel = false;
    efiOrder({ kit: kitId, ...(coupon ? { coupon } : {}) })
      .then(res => {
        if (cancel) return;
        setOrder(res);
        setStatus('pending');
        setRemaining(res.expiresIn);
        startedAt.current = Date.now();
      })
      .catch((err: any) => {
        if (cancel) return;
        notify('error', err?.message || err?.detail || 'Erro ao criar cobranca PIX');
        setStatus('error');
      });
    return () => { cancel = true; };
  }, [kitId, notify]);

  // 2) Polling de status
  useEffect(() => {
    if (status !== 'pending' || !order) return;

    const tick = async () => {
      try {
        const r = await efiStatus(order.txid);
        if (r.status === 'paid') {
          setStatus('paid');
          if (pollTimer.current) clearInterval(pollTimer.current);
          notify('success', 'Pagamento confirmado!');
          onPaid();
        } else if (r.status === 'expired' || r.status === 'canceled') {
          setStatus('expired');
          if (pollTimer.current) clearInterval(pollTimer.current);
        }
      } catch {
        // Ignora erros transientes - polling continua
      }
      // Timeout hard
      if (Date.now() - startedAt.current > POLL_TIMEOUT_MS) {
        setStatus('expired');
        if (pollTimer.current) clearInterval(pollTimer.current);
      }
    };

    pollTimer.current = setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      if (pollTimer.current) clearInterval(pollTimer.current);
    };
  }, [status, order, notify, onPaid]);

  // 3) Timer de expiracao
  useEffect(() => {
    if (status !== 'pending') return;
    const t = setInterval(() => {
      setRemaining(s => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(t);
  }, [status]);

  const copy = async () => {
    if (!order) return;
    try {
      await navigator.clipboard.writeText(order.qrCodePayload);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      notify('error', 'Nao foi possivel copiar');
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-md" />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
           onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div className="surface-card rounded-2xl p-8 max-w-md w-full relative" onClick={e => e.stopPropagation()}>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/65 hover:text-white transition-colors cursor-pointer"
          >
            <X size={20} />
          </button>

          {/* Header */}
          <div className="text-center mb-5">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-[#8fb9de]/10 flex items-center justify-center mb-3">
              <QrCode size={32} className="text-[#8fb9de]" />
            </div>
            <h3 className="text-xl font-bold text-white">Pagamento via PIX</h3>
            <p className="text-white/65 text-sm mt-1">
              <span className="text-white font-bold">{kitCash.toLocaleString('pt-BR')} lcoin</span>
              {' '} - {' '} <span className="text-white font-bold">{kitTitle}</span>
            </p>
            {order && (
              <p className="text-xs text-white/50 mt-1">
                Total: <span className="text-white">{formatBRL(order.valor)}</span>
              </p>
            )}
          </div>

          {/* Loading */}
          {status === 'loading' && (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={28} className="animate-spin text-[#8fb9de]" />
              <span className="ml-3 text-sm text-white/65">Gerando QR code...</span>
            </div>
          )}

          {/* Error */}
          {status === 'error' && (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <AlertCircle size={36} className="text-red-400 mb-3" />
              <p className="text-sm text-white/80">Nao foi possivel gerar a cobranca.</p>
              <button
                onClick={onClose}
                className="mt-4 px-4 py-2 rounded-lg bg-white/10 text-white text-sm hover:bg-white/15 cursor-pointer"
              >Fechar</button>
            </div>
          )}

          {/* Paid */}
          {status === 'paid' && (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <CheckCircle2 size={48} className="text-green-400 mb-3" />
              <p className="text-lg font-bold text-white">Pagamento confirmado!</p>
              <p className="text-sm text-white/65 mt-1">lcoin creditado na sua conta.</p>
              <button
                onClick={onClose}
                className="mt-5 px-6 py-2 rounded-lg bg-[#8fb9de] text-[#021415] font-bold text-sm hover:bg-[#4f78a8] cursor-pointer"
              >Fechar</button>
            </div>
          )}

          {/* Expired */}
          {status === 'expired' && (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <AlertCircle size={36} className="text-orange-400 mb-3" />
              <p className="text-sm text-white/80">QR code expirou. Crie um novo pedido pra continuar.</p>
              <button
                onClick={onClose}
                className="mt-4 px-4 py-2 rounded-lg bg-white/10 text-white text-sm hover:bg-white/15 cursor-pointer"
              >Fechar</button>
            </div>
          )}

          {/* Pending */}
          {status === 'pending' && order && (
            <>
              {/* QR Image */}
              {order.qrCodeImage && (
                <div className="flex justify-center mb-4">
                  <div className="bg-white p-3 rounded-xl">
                    <img
                      src={order.qrCodeImage}
                      alt="QR Code PIX"
                      width={220}
                      height={220}
                      className="block"
                    />
                  </div>
                </div>
              )}

              {/* Copia e Cola */}
              <div className="mb-4">
                <p className="text-xs text-white/65 mb-1 uppercase tracking-wider font-bold">
                  Codigo PIX copia e cola
                </p>
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={order.qrCodePayload}
                    className="flex-1 min-w-0 px-3 py-2 rounded-lg bg-white/5 text-white text-xs font-mono truncate border border-white/10"
                  />
                  <button
                    onClick={copy}
                    className="px-3 py-2 rounded-lg bg-[#8fb9de]/15 text-[#8fb9de] hover:bg-[#8fb9de]/25 transition-colors flex items-center gap-1 text-sm font-bold cursor-pointer"
                  >
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                    {copied ? 'Copiado' : 'Copiar'}
                  </button>
                </div>
              </div>

              {/* Timer + status */}
              <div className="flex items-center justify-between text-xs text-white/65 border-t border-white/10 pt-3">
                <span className="flex items-center gap-2">
                  <Loader2 size={12} className="animate-spin" />
                  Aguardando pagamento...
                </span>
                <span className="font-mono">Expira em {formatTimer(remaining)}</span>
              </div>

              <p className="text-[11px] text-white/45 mt-3 leading-snug text-center">
                Abra o app do seu banco, escolha PIX e escaneie o QR ou cole o codigo.
                A confirmacao e automatica.
              </p>
            </>
          )}
        </div>
      </div>
    </>
  );
}
